import React from 'react'
import styled from 'styled-components'

import Header, { Title } from '../Header'
import { AttributeList } from '../Attribute'
import Section from '../Section'
import Separator from '../Separator'
import FullCopyAttribute from '../FullCopyAttribute'
import { Flex } from '@sketch/components'

import copy from './copy'
import { capitalize, roundWithLocale } from '@sketch/utils'

import { Layer } from 'modules/shares/types'

import { ReactComponent as CornerRoundedIcon } from '@sketch/icons/corner.rounded.16'
import { ReactComponent as CornerAngledIcon } from '@sketch/icons/corner.angled.16'
import { ReactComponent as CornerSmoothIcon } from '@sketch/icons/corner.smooth.16'
import { ReactComponent as CornerInsideArcIcon } from '@sketch/icons/corner.inside.arc.16'
import { ReactComponent as CornerInsideSquareIcon } from '@sketch/icons/corner.inside.square.16'

interface CornersProps {
  radius: number[]
  cornerStyle?: Layer['cornerStyle']
  smoothCorners?: boolean
}

const CornerStyleText = styled.div`
  margin-left: 4px;
  line-height: 1.2;
`

const cornerStyleIcon: Record<
  NonNullable<Layer['cornerStyle']>,
  typeof CornerRoundedIcon
> = {
  rounded: CornerRoundedIcon,
  smooth: CornerSmoothIcon,
  angled: CornerAngledIcon,
  'inside arc': CornerInsideArcIcon,
  'inside square': CornerInsideSquareIcon,
}

/**
 * Several layer types can have corners (rectangle, triangle, polygon or star),
 * and we also manage 2 ways of showing them, the deprecated `smoothCorners` or
 * the new one based on `cornerStyle` (coming from `layer.cornerStyle`).
 */
export const Corners: React.FC<CornersProps> = ({
  radius,
  cornerStyle,
  smoothCorners,
}) => {
  const hasRadius = radius.some(n => n !== 0)

  if (!hasRadius) {
    return null
  }

  // If there is a radius but style is not defined, it means is an old document
  // and the corner style logic is different
  const style = cornerStyle ?? (smoothCorners ? 'smooth' : undefined)

  // Normalized radius is the radius array simplified to have only one element
  // if all elements are the same
  const normalizedRadius = (hasRadius && radius.every(r => r === radius[0])
    ? [radius[0]]
    : radius
  )
    .map(r => roundWithLocale(r, 2))
    .join(';')

  const sectionCopyValue = copy({
    radius: normalizedRadius,
    style,
  })

  // `CornerRoundedIcon` will never be reached in this ternary, because we only
  // render Icon if `style` is truthy
  const Icon = style ? cornerStyleIcon[style] : CornerRoundedIcon

  return (
    <>
      <Separator />
      <Section data-testid="inspector-sidebar-corners">
        <Header copyValue={sectionCopyValue}>
          <Title>Corners</Title>
        </Header>
        <AttributeList>
          <FullCopyAttribute
            label="Radius"
            value={normalizedRadius}
            copyValue={normalizedRadius}
          />
          {style && (
            <FullCopyAttribute
              label="Style"
              value={
                <Flex alignItems="center">
                  <Icon width={16} />
                  <CornerStyleText>{capitalize(style)}</CornerStyleText>
                </Flex>
              }
              copyValue={capitalize(style)}
            />
          )}
        </AttributeList>
      </Section>
    </>
  )
}
